import { rootApi } from 'src/services/rootApi';
import { HTTPRequestType } from 'src/services/api.types';

/**
 * The Claude connection: a recruiter's own Claude reading JobTetris (JT1-1787). The Allow page
 * (JT1-1832) and the agency admin section in AI settings (JT1-1833). Mirrors `McpAdminController`.
 */

export interface McpSettings {
  /** JobTetris has released the connection. Until then the section says "Coming soon". */
  released: boolean;
  /** The agency has the AI layer. Without it the section is left out. */
  ai_layer: boolean;
  available: boolean;
  enabled: boolean;
  terms_version: string;
  terms_accepted: boolean;
  terms_accepted_at: string | null;
  terms_accepted_by: string | null;
  mcp_url: string;
}

export interface McpConnection {
  id: string;
  account_id: string;
  person: string | null;
  client_name: string;
  connected_at: string;
  last_used_at: string | null;
}

export type McpAuditOutcome = 'pending' | 'ok' | 'error' | 'refused' | 'limited';

export interface McpAuditEntry {
  id: string;
  created_at: string;
  account_id: string;
  person: string | null;
  tool: string;
  input: Record<string, unknown> | null;
  rows_returned: number;
  outcome: McpAuditOutcome;
  report_scope: string | null;
  report_id: string | null;
}

export interface McpAuditPage {
  results: McpAuditEntry[];
  count: number;
}

/** What the Allow page shows about a sign-in request from Claude. */
export interface McpAuthorizeRequest {
  client_name: string;
  redirect_host: string;
  /** The code goes back to an app on this computer (Claude Desktop or Claude Code), not to claude.ai. */
  local_app: boolean;
  /** The agency the page shows. Sent back with Allow, so switching agency in another tab is caught. */
  organization_id: string;
  agency: string | null;
  person: string | null;
  /** The agency's connection is switched on. */
  open: boolean;
  admin: boolean;
}

export const aiMcpApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getMcpSettings: builder.query<McpSettings, void>({
      query: () => ({ url: '/ai/mcp/settings' }),
      providesTags: ['McpSettings'],
    }),

    acceptMcpTerms: builder.mutation<McpSettings, void>({
      query: () => ({ url: '/ai/mcp/settings/accept-terms', method: HTTPRequestType.POST }),
      invalidatesTags: ['McpSettings'],
    }),

    setMcpEnabled: builder.mutation<McpSettings, boolean>({
      query: (enabled) => ({ url: '/ai/mcp/settings', method: HTTPRequestType.PUT, data: { enabled } }),
      invalidatesTags: ['McpSettings'],
    }),

    getMcpConnections: builder.query<McpConnection[], { all: boolean }>({
      query: ({ all }) => ({ url: '/ai/mcp/connections', params: all ? { all: 'true' } : {} }),
      providesTags: ['McpConnections'],
    }),

    disconnectMcp: builder.mutation<void, string>({
      query: (id) => ({ url: `/ai/mcp/connections/${id}`, method: HTTPRequestType.DELETE }),
      invalidatesTags: ['McpConnections'],
    }),

    getMcpAudit: builder.query<McpAuditPage, { page: number; per_page: number; account_id?: string }>({
      query: (params) => ({ url: '/ai/mcp/audit', params }),
      providesTags: ['McpAudit'],
    }),

    getMcpAuthorizeRequest: builder.query<McpAuthorizeRequest, string>({
      query: (request) => ({ url: '/ai/mcp/oauth/request', params: { request } }),
    }),

    approveMcpRequest: builder.mutation<{ redirect_url: string }, { request: string; organization_id: string }>({
      query: (data) => ({ url: '/ai/mcp/oauth/approve', method: HTTPRequestType.POST, data }),
      invalidatesTags: ['McpConnections'],
    }),

    denyMcpRequest: builder.mutation<{ redirect_url: string }, string>({
      query: (request) => ({ url: '/ai/mcp/oauth/deny', method: HTTPRequestType.POST, data: { request } }),
    }),
  }),
});

export const {
  useGetMcpSettingsQuery,
  useAcceptMcpTermsMutation,
  useSetMcpEnabledMutation,
  useGetMcpConnectionsQuery,
  useDisconnectMcpMutation,
  useGetMcpAuditQuery,
  useGetMcpAuthorizeRequestQuery,
  useApproveMcpRequestMutation,
  useDenyMcpRequestMutation,
} = aiMcpApi;
